import { useState } from 'react';

import type { StepDisplay } from '@poseidon/components/CampaignDayCard';
import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { MarkDonePopup } from '@poseidon/components/Popup';
import { StepIcon } from '@poseidon/components/StepIcon';
import { General } from '@poseidon/components/StrokeIcons';
import { getTemplateStepDisplay } from '@poseidon/components/TemplateBlockCard';

import { MinimalAddTile } from './MinimalAddTile';
import type { TouchGraphNode, TouchGraphStep, TouchStepContactStatus } from './touchGraphLayout';
import { touchStepsAreTerminal } from './touchGraphLayout';

/** One plain-text run or one resolved variable mention in a step preview. */
type PreviewSegment = NonNullable<StepDisplay['primarySegments']>[number];

/**
 * Visual bucket for a step's per-contact status. The buckets must be told apart at a
 * glance: `action` (you still have to do something) is BLUE and carries a button;
 * `done` (finished / replied) is solid GREEN; `skipped` is green struck through with a
 * 45° diagonal hatch (the sequence auto-advanced past it — on the happy path, but this
 * touch never actually ran); `error` is red; the remaining skip-ish states (canceled /
 * interrupted / unavailable) are muted gray. `not-started` (and anything unmapped)
 * returns null → the row renders neutral, exactly as the template page does. An error
 * message always wins (a step can carry one in a non-terminal state).
 */
type StatusTone = 'done' | 'action' | 'error' | 'muted' | 'skipped';

function stepStatusTone(status: TouchStepContactStatus | null): { tone: StatusTone; label: string } | null {
  if (status == null) return null;
  if (status.errorMessage != null) return { tone: 'error', label: 'Errored' };
  switch (status.state) {
    case 'action-required':
      return { tone: 'action', label: 'Action needed' };
    case 'finished':
      return { tone: 'done', label: 'Done' };
    case 'responded':
      return { tone: 'done', label: 'Replied' };
    case 'skipped':
      return { tone: 'skipped', label: 'Skipped' };
    case 'canceled':
      return { tone: 'muted', label: 'Canceled' };
    case 'contact_unavailable':
      return { tone: 'muted', label: 'Unavailable' };
    case 'interrupted':
      return { tone: 'muted', label: 'Interrupted' };
    default:
      return null; // not-started → neutral
  }
}

/**
 * 45° diagonal hatch — 1.5px mantis lines every 5px — layered as a background *image*
 * over a green fill (the fill is a separate `bg-*` color, which shows through the
 * transparent gaps). Marks a SKIPPED step: green because the sequence advanced past it
 * cleanly (unlike a red error or a gray cancel), struck through with diagonal lines
 * because this touch never actually ran for the contact. A lighter line in dark mode so
 * it reads on the darker green base. Same technique as `CounterBox`'s disabled hatch.
 * Tune geometry here: `_3.5px` = gap, `_5px` = period (line width = 5 − 3.5 = 1.5px).
 */
const SKIPPED_HATCH =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-500)_3.5px,var(--color-mantis-500)_5px)] ' +
  'dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-400)_3.5px,var(--color-mantis-400)_5px)]';

/**
 * Row background tint for a status tone. `done` is empty (it reads via its green
 * badge); `action` gets a blue wash so a step you still owe an action on stands
 * apart from a finished (green) one. `skipped` gets a soft green fill under the hatch.
 */
const TONE_ROW_CLASS: Record<StatusTone, string> = {
  done: '',
  action: 'bg-blue-50 dark:bg-blue-500/10',
  error: 'ring-1 ring-inset ring-fair-pink-300 dark:ring-fair-pink-500/40',
  muted: 'opacity-60',
  skipped: cn('bg-mantis-50 dark:bg-mantis-950', SKIPPED_HATCH),
};

/** Small status pill shown next to the step-type label — blue = act, green = done. */
const TONE_BADGE_CLASS: Record<StatusTone, string> = {
  done: 'bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200',
  action: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  error: 'bg-fair-pink-100 text-fair-pink-700 dark:bg-fair-pink-950 dark:text-fair-pink-300',
  muted: 'bg-lynch-100 text-lynch-600 dark:bg-slate-800 dark:text-lynch-300',
  // Solid green pill; the "Skipped" label (not the hatch) tells it apart from "Done"
  // so the small pill text stays legible.
  skipped: 'bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200',
};

/** Minimal-mode icon-chip tint by status tone (falls back to the neutral chip). */
const TONE_CHIP_CLASS: Record<StatusTone, string> = {
  done: 'bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200',
  action: 'bg-blue-100 text-blue-700 ring-1 ring-blue-300 dark:bg-blue-900 dark:text-blue-200',
  error: 'bg-fair-pink-100 text-fair-pink-600 dark:bg-fair-pink-950 dark:text-fair-pink-300',
  muted: 'bg-lynch-50 text-lynch-400 opacity-60 dark:bg-slate-800',
  // Same green as `done`, struck through with the diagonal hatch; the step icon renders
  // on top (foreground), the hatch/fill sit behind it as backgrounds.
  skipped: cn('bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200', SKIPPED_HATCH),
};

const STEP_TYPE_LABEL: Partial<Record<TouchGraphStep['type'], string>> = {
  message: 'LinkedIn message',
  inmail: 'InMail',
  email: 'Email',
  connect: 'Connection request',
  follow: 'Follow',
  visit: 'Profile visit',
  likeRecentPosts: 'Like recent posts',
  reminder: 'Manual task',
  sift: 'Filter',
  call: 'Call',
  voice_note: 'Voice note',
  switch_campaign: 'Switch campaign',
};

interface Props {
  node: TouchGraphNode;
  /** The step currently open in the editor panel (highlighted), if any. */
  selectedStepId?: string | null;
  /** Omit to render read-only — step rows/card aren't clickable. */
  onSelectStep?: (stepId: string) => void;
  /** Omit to hide the delete control (read-only). */
  onRequestDelete?: () => void;
  /** Add another step to THIS group (same day). Omit to hide the add-step row. */
  onAddStepToGroup?: () => void;
  /**
   * Complete an action-required step for THIS contact — passed the contact's own
   * cadence-step id (`step.contactStatus.cadenceStepId`). Only wired on the contact
   * page; omit on the template page (no per-contact status, so no button renders).
   */
  onMarkStepFinished?: (cadenceStepId: string) => void;
  /**
   * Compact rendering: the whole touch is a single headerless row of step-type
   * icon chips (no "Week N" header, no preview text, no add-step footer). Matches
   * the height the layout reserves for a minimal node. Full card when false.
   */
  minimal?: boolean;
}

/**
 * A single touch on the canvas — a compact preview card of header + step rows.
 * Clicking a step row opens it in the editor panel (kept at 100% scale,
 * off-canvas). The "add step" affordance lives in the below-card region owned by
 * the canvas, not here. The card is positioned by the parent canvas.
 */
export function TouchNodeCard({
  node,
  selectedStepId = null,
  onSelectStep,
  onRequestDelete,
  onAddStepToGroup,
  onMarkStepFinished,
  minimal = false,
}: Props) {
  const isSelected = node.steps.some((s) => s.id === selectedStepId);

  const firstStepId = node.steps[0]?.id ?? null;
  const selectable = firstStepId != null && onSelectStep != null;

  // A switch_campaign step is a terminal "move the contact to another campaign" action,
  // so it must be the only step in its touch — once one is present, hide the add-step
  // affordance (in both minimal and full modes) so no sibling can be added.
  const hasSwitchCampaign = touchStepsAreTerminal(node.steps);

  // Marking a step done goes through a confirmation popup ("This can't be undone").
  // We track the cadence-step id whose popup is open; requesting one opens it, and
  // the popup's confirm calls `onMarkStepFinished`. Only wired on the contact page —
  // `onRequestMarkDone` is undefined without a handler, so no trigger renders.
  const [markDoneCadenceStepId, setMarkDoneCadenceStepId] = useState<string | null>(null);
  const onRequestMarkDone =
    onMarkStepFinished != null ? (cadenceStepId: string) => setMarkDoneCadenceStepId(cadenceStepId) : undefined;
  const closeMarkDone = () => setMarkDoneCadenceStepId(null);
  const confirmMarkDone = () => {
    if (markDoneCadenceStepId != null) onMarkStepFinished?.(markDoneCadenceStepId);
    setMarkDoneCadenceStepId(null);
  };

  // Minimal: a compact touch. There is no shared white "group box": a SINGLE step is
  // just its icon; several same-day steps are individual item cards; an empty touch
  // keeps a small boxed "Empty" placeholder. Branch structure (No branch, fork arms)
  // shows via the canvas layout + edges — each branch touch is its own compact node.
  if (minimal) {
    // First action-required step in this touch — the mark-done target. Marking it
    // surfaces the next one on the next render, so one button walks a touch that
    // (rarely) holds several manual steps. Only when a handler is wired (contact page).
    const actionStep =
      onRequestMarkDone != null ? (node.steps.find((s) => s.contactStatus?.state === 'action-required') ?? null) : null;
    const actionCadenceStepId = actionStep?.contactStatus?.cadenceStepId ?? null;
    // >1 step → the icons share ONE block; exactly 1 → just the bare icon; 0 → the boxed
    // placeholder. A single step and the "+" add-step chip carry no box.
    const asItems = node.steps.length > 1;
    const isEmpty = node.steps.length === 0;
    const singleStep = node.steps.length === 1 ? (node.steps[0] ?? null) : null;
    const singleChipTone = singleStep != null ? stepStatusTone(singleStep.contactStatus)?.tone : undefined;
    const cardClickable = selectable && !asItems;
    return (
      <>
        <div
          data-node="touch"
          data-testid={`touch-node-${node.blockId}`}
          // Disabled while the confirm popup is open: its clicks bubble through the React
          // portal tree back to this card, and confirming shouldn't also open the editor.
          onClick={cardClickable && markDoneCadenceStepId == null ? () => onSelectStep(firstStepId) : undefined}
          className={cn(
            'group/node flex w-full flex-col items-stretch gap-1.5',
            cardClickable && 'cursor-pointer',
            // Only the empty placeholder gets a box on the wrapper — a single step is just
            // its icon and a multi-step touch draws its own block, so the wrapper stays bare.
            isEmpty &&
              'overflow-hidden rounded-xl border border-lynch-200 bg-white px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900',
          )}>
          <div className="flex flex-row items-center justify-center gap-1.5">
            {isEmpty && <span className="text-[11px] italic text-lynch-400 dark:text-lynch-500">Empty</span>}

            {/* Single step → JUST the step's icon, no card chrome (per design). The wrapper
                owns the click; selection + hover live on the chip itself so hovering the
                sibling "+" add-step chip doesn't light the step up. */}
            {singleStep != null && (
              <span
                data-testid={`touch-step-icon-${singleStep.id}`}
                data-status={singleStep.contactStatus?.state ?? undefined}
                className={cn(
                  'flex size-11 shrink-0 items-center justify-center rounded-xl transition-shadow',
                  singleChipTone != null ? TONE_CHIP_CLASS[singleChipTone] : 'bg-lynch-100 dark:bg-slate-800',
                  isSelected ? 'ring-2 ring-blue-400/60' : cardClickable && 'hover:ring-2 hover:ring-blue-300/60',
                )}>
                <StepIcon iconType={singleStep.type} className="size-5" />
              </span>
            )}

            {/* Multiple steps → all icons share ONE block (not a card per step). Each chip
                is clickable to open its specific step; hover/selection ring the chip alone. */}
            {asItems && (
              <div
                data-testid={`touch-step-group-${node.blockId}`}
                className="flex flex-row items-center gap-1 rounded-xl border border-lynch-200 bg-white p-1.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                {node.steps.map((step) => {
                  const chipTone = stepStatusTone(step.contactStatus)?.tone;
                  return (
                    <span
                      key={step.id}
                      data-testid={`touch-step-icon-${step.id}`}
                      data-status={step.contactStatus?.state ?? undefined}
                      onClick={
                        onSelectStep != null
                          ? (e) => {
                              e.stopPropagation();
                              onSelectStep(step.id);
                            }
                          : undefined
                      }
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-lg transition-shadow',
                        onSelectStep != null && 'cursor-pointer',
                        chipTone != null ? TONE_CHIP_CLASS[chipTone] : 'bg-lynch-50 dark:bg-slate-800',
                        step.id === selectedStepId
                          ? 'ring-2 ring-blue-400/60'
                          : onSelectStep != null && 'hover:ring-2 hover:ring-blue-300/60',
                      )}>
                      <StepIcon iconType={step.type} className="size-4" />
                    </span>
                  );
                })}
              </div>
            )}
            {/* Add another step to THIS touch (same day) — the same shared dashed-circle
                "+" tile used for the trailing / fork-arm add nodes. Every add reads as
                one control. */}
            {onAddStepToGroup != null && node.steps.length > 0 && !hasSwitchCampaign && (
              <MinimalAddTile
                testId={`touch-add-step-${node.blockId}`}
                tooltip="Add a step to this touch"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddStepToGroup();
                }}
              />
            )}
          </div>
          {/* Compact "Mark done" for an action-required step — opens the confirm popup
              (there's no room for the full expanded status row in a minimal card). The
              layout reserves this row's height, so it never overlaps the next touch. */}
          {actionStep != null && actionCadenceStepId != null && onRequestMarkDone != null && (
            <Button
              data-testid={`touch-step-mark-done-${actionStep.id}`}
              size="2xs"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                onRequestMarkDone(actionCadenceStepId);
              }}
              className="w-full">
              Mark done
            </Button>
          )}
        </div>
        {markDoneCadenceStepId != null && <MarkDonePopup onClose={closeMarkDone} onSubmit={confirmMarkDone} />}
      </>
    );
  }

  return (
    <div
      data-node="touch"
      data-testid={`touch-node-${node.blockId}`}
      // Clicking anywhere on the card opens its (single) touch; step rows below
      // stop propagation so multi-step blocks can still target a specific step.
      // Disabled while the confirm popup is open — its portal clicks bubble back here
      // through the React tree, and confirming shouldn't also open the editor.
      onClick={selectable && markDoneCadenceStepId == null ? () => onSelectStep(firstStepId) : undefined}
      className={cn(
        selectable && 'cursor-pointer',
        // Height is content-driven (w-full, not size-full) so the card never leaves
        // trailing white space below the footer when node.h over-reserves.
        'group/node flex w-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition-colors dark:bg-slate-900',
        isSelected
          ? 'border-blue-400 ring-2 ring-blue-400/40 dark:border-blue-500'
          : 'border-lynch-200 hover:border-blue-300 dark:border-slate-700 dark:hover:border-blue-500/60',
      )}>
      <div className="flex items-center gap-2 border-b border-lynch-100 px-3 py-2 dark:border-slate-800">
        <span className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{node.label}</span>
        {onRequestDelete != null && (
          <Button
            data-testid={`touch-node-delete-${node.blockId}`}
            icon={<General.Trash01 />}
            text
            size="xs"
            color="red"
            tone="soft"
            tooltip="Delete this touch"
            onClick={(e) => {
              e.stopPropagation();
              onRequestDelete();
            }}
            className="ml-auto opacity-0 transition-opacity group-hover/node:opacity-100"
          />
        )}
      </div>

      <div className="flex flex-col">
        {node.steps.length === 0 && (
          <div className="flex items-center justify-center px-3 py-5 text-xs italic text-lynch-400 dark:text-lynch-500">
            Empty touch
          </div>
        )}
        {node.steps.map((step, i) => (
          <StepPreviewRow
            key={step.id}
            step={step}
            selected={step.id === selectedStepId}
            isFirst={i === 0}
            onClick={onSelectStep != null ? () => onSelectStep(step.id) : undefined}
            onRequestMarkDone={onRequestMarkDone}
          />
        ))}

        {node.waitsForConnect && (
          <span
            data-testid={`touch-wfc-${node.blockId}`}
            className="flex items-center gap-1.5 border-t border-lynch-100 px-3 py-1.5 text-[11px] font-medium text-blue-600 dark:border-slate-800 dark:text-blue-300">
            <StepIcon iconType="connect" className="size-3.5 shrink-0" />
            Then waits for the invite to be accepted
          </span>
        )}

        {/* Small add-another-step row at the bottom of the group — a thin, faint strip
            that lights up on hover so it stays unobtrusive until you reach for it. */}
        {onAddStepToGroup != null && node.steps.length > 0 && !hasSwitchCampaign && (
          <Button
            data-testid={`touch-add-step-${node.blockId}`}
            icon={<General.Plus className="size-3 shrink-0" />}
            text
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              onAddStepToGroup();
            }}
            className={cn(
              'w-full justify-center gap-1 rounded-none border-t border-lynch-100 py-1 text-[11px] font-medium',
              'text-lynch-400 opacity-50 transition-all hover:bg-blue-50 hover:text-blue-600 hover:opacity-100',
              'dark:border-slate-800 dark:text-lynch-500 dark:hover:bg-blue-500/10 dark:hover:text-blue-300',
            )}>
            add step
          </Button>
        )}
      </div>

      {markDoneCadenceStepId != null && <MarkDonePopup onClose={closeMarkDone} onSubmit={confirmMarkDone} />}
    </div>
  );
}

function StepPreviewRow({
  step,
  selected,
  isFirst,
  onClick,
  onRequestMarkDone,
}: {
  step: TouchGraphStep;
  selected: boolean;
  /** All but the first step get a divider line above them. */
  isFirst: boolean;
  /** Omit to render read-only (row isn't clickable). */
  onClick?: () => void;
  /** Open the mark-done confirm popup for this contact's step. Omit on the template page. */
  onRequestMarkDone?: (cadenceStepId: string) => void;
}) {
  const display = getTemplateStepDisplay(step);
  // switch_campaign carries no message body — its "content" is the campaign it moves the
  // contact into. The destination name(s) are resolved at model-build time; empty means
  // none configured yet. Shown in place of the raw step display so the card reads
  // "Switch campaign" / "<destination>" instead of the snake_case type twice.
  const isSwitchCampaign = step.type === 'switch_campaign';
  const switchDestination =
    step.triggerCadenceTemplateNames.length > 0 ? step.triggerCadenceTemplateNames.join(', ') : 'No destination set';
  const status = step.contactStatus;
  const visual = stepStatusTone(status);
  // Action-required steps get a "Mark done" button — but only where a handler is
  // wired (contact page). No handler → no button (never a dead control).
  const showMarkDone = status != null && status.state === 'action-required' && onRequestMarkDone != null;
  return (
    <div
      data-testid={`touch-step-${step.id}`}
      data-status={status?.state ?? undefined}
      onClick={
        onClick == null
          ? undefined
          : (e) => {
              e.stopPropagation();
              onClick();
            }
      }
      className={cn(
        // Full-bleed row (no inset/rounded) — bg + dividers span the whole card width.
        'flex min-w-0 items-start gap-2 px-3 py-2 text-left transition-colors',
        onClick != null && 'cursor-pointer',
        !isFirst && 'border-t border-lynch-100 dark:border-slate-800',
        // Selection tint wins; otherwise the per-contact status tint; otherwise hover.
        selected
          ? 'bg-blue-50 dark:bg-blue-500/10'
          : cn(
              visual != null && TONE_ROW_CLASS[visual.tone],
              onClick != null && 'hover:bg-lynch-50 dark:hover:bg-slate-800/60',
            ),
        step.error != null && 'ring-1 ring-inset ring-red-300 dark:ring-red-500/40',
      )}>
      <StepIcon iconType={step.type} className="mt-0.5 size-4 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[11px] font-medium text-gray-500 dark:text-gray-400">
            {STEP_TYPE_LABEL[step.type] ?? step.type}
          </span>
          {visual != null && (
            <span
              data-testid={`touch-step-status-${step.id}`}
              className={cn(
                'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                TONE_BADGE_CLASS[visual.tone],
              )}>
              {visual.label}
            </span>
          )}
        </div>
        <span className="line-clamp-1 text-xs leading-snug text-gray-800 dark:text-gray-100">
          {isSwitchCampaign && switchDestination}
          {!isSwitchCampaign && <PreviewSegments segments={display.primarySegments} plain={display.primary} />}
        </span>
        {/* Email / InMail rows keep the subject on the primary line and carry the
            message body separately — show it (clamped) so reps can preview the email
            content without opening the editor. The clamp mirrors STEP_PREVIEW_MAX_LINES
            in touchGraphLayout, which reserves the matching row height. */}
        {display.body != null && display.body.length > 0 && (
          <span
            data-testid="touch-step-body"
            // `whitespace-pre-wrap` so the paragraph/line breaks the body carries
            // (htmlToSegments turns `</p>`, `<br>` into real `\n`) render as
            // separate lines instead of collapsing into one run. `line-clamp-6`
            // still caps the height — it just clamps wrapped + hard-broken lines.
            className="mt-0.5 line-clamp-6 whitespace-pre-wrap text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            <PreviewSegments segments={display.bodySegments} plain={display.body} />
          </span>
        )}
        {step.error != null && (
          <span className="mt-0.5 line-clamp-1 text-[11px] text-red-500 dark:text-red-400">{step.error}</span>
        )}
        {/* This contact's runtime error (distinct from the template validation error
            above) — e.g. a send failure. Clamped so a long message can't grow the row
            past the height the layout reserved. */}
        {status?.errorMessage != null && (
          <span
            data-testid={`touch-step-error-${step.id}`}
            className="mt-0.5 line-clamp-2 text-[11px] text-fair-pink-600 dark:text-fair-pink-400">
            {status.errorMessage}
          </span>
        )}
        {/* Why the step was skipped, when the server recorded a reason. */}
        {status?.errorMessage == null && status?.state === 'skipped' && status.skipReason != null && (
          <span
            data-testid={`touch-step-skip-${step.id}`}
            className="mt-0.5 line-clamp-2 text-[11px] text-lynch-500 dark:text-lynch-400">
            {status.skipReason}
          </span>
        )}
        {showMarkDone && (
          <div className="mt-1.5 flex justify-end">
            <Button
              data-testid={`touch-step-mark-done-${step.id}`}
              size="xs"
              color="blue"
              onClick={(e) => {
                // Don't let the click bubble to the row (which opens the editor).
                e.stopPropagation();
                onRequestMarkDone(status.cadenceStepId);
              }}>
              Mark done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Render a step's resolved content: variable mentions as purple pills, plain
 * runs as text. Shared by the primary (subject / message body) line and the
 * secondary email/inmail body block. Falls back to the plain string when the
 * content carried no variable segments.
 */
function PreviewSegments({ segments, plain }: { segments: PreviewSegment[] | undefined; plain: string }) {
  if (segments == null) return <>{plain}</>;
  return (
    <>
      {segments.map((seg, i) => (
        <span
          key={i}
          data-testid={seg.kind === 'variable' ? 'touch-step-variable' : undefined}
          className={cn(
            seg.kind === 'variable' &&
              'rounded bg-cold-purple-100 px-1 font-medium text-cold-purple-700 dark:bg-cold-purple-500/25 dark:text-cold-purple-200',
          )}>
          {seg.text}
        </span>
      ))}
    </>
  );
}
